import { Component, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AutoCompleteModule, AutoCompleteSelectEvent } from 'primeng/autocomplete';

import { CaptureDataFormService } from '../capture-data-form/capture-data-form.service';
import { TooltipService } from '../shared/tooltip/tooltip.service';
import { EXCEPTION_OPTIONS } from './exception-options.constants';
import { formatConfidence } from '../shared/utils/confidence-utils';
import { VW_VISIBLE_HEADER_FIELDS } from '../shared/constants/vw-header-visible-fields.constants'
import { INVOICE_FIELD_LABELS } from '../shared/constants/invoice-field-label-mapping';

const LOW_CONFIDENCE_STYLE_CLASS = 'capture-headers-form-item--warning';

@Component({
  selector: 'app-capture-data-headers-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    TooltipModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    AutoCompleteModule,
    TranslateModule,
  ],
  templateUrl: './capture-data-headers-form.component.html',
  styleUrl: './capture-data-headers-form.component.css',
})
export class CaptureDataHeadersFormComponent implements OnInit {
  headerCaptureDataForm = input.required<FormGroup>();

  readonly INVOICE_FIELD_LABELS = INVOICE_FIELD_LABELS;
  private readonly captureDataForm = inject(CaptureDataFormService);
  private readonly tooltipService = inject(TooltipService);
  private readonly translate = inject(TranslateService);

  exceptionOptions = EXCEPTION_OPTIONS;
  translatedExceptionOptions: { key: string; label: string }[] = [];
  filteredExceptionOptions: { key: string; label: string }[] = [];
  exceptionMessage = '';

  suppliers = [
    { name: 'Acme Corp', number: 'SUP-1001' },
    { name: 'Globex Inc.', number: 'SUP-2022' },
    { name: 'Initech', number: 'SUP-3033' },
  ];

  filteredSuppliers: { name: string; number: string }[] = [];
  selectedSupplierName = '';
  selectedSupplierNumber = '';

  ngOnInit(): void {
    const form = this.headerCaptureDataForm();
    const supplierNameFromApi = form.get('supplier_name')?.get('value')?.value;
    if (supplierNameFromApi) {
      this.selectedSupplierName = supplierNameFromApi;
    }

    this.selectedSupplierNumber = '';

    this.translatedExceptionOptions = this.exceptionOptions.map(key => ({
      key,
      label: this.translate.instant(`exceptions.${key}`),
    }));
  }

  shouldDisplayField(prop: string): boolean {
    return VW_VISIBLE_HEADER_FIELDS.has(prop);
  }

  formatConfidenceValue(conf: number | null | undefined): string {
    return formatConfidence(conf);
  }

  filterExceptions(event: { query: string }) {
    const query = event.query.toLowerCase();
    this.filteredExceptionOptions = this.translatedExceptionOptions.filter(option =>
      option.label.toLowerCase().includes(query)
    );
  }

  onExceptionSelected(event: AutoCompleteSelectEvent) {
    const selected = event.value as { key: string; label: string };
    if (selected?.key) {
      this.exceptionMessage = selected.key;
    }
  }

  filterSuppliers(event: { query: string }) {
    const query = event.query.toLowerCase();
    if (query.length < 3) {
      this.filteredSuppliers = [];
      return;
    }

    const matches = this.suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(query)
    );
    this.filteredSuppliers = matches.length ? matches : [{ name: '__noMatch__', number: '' }];
  }

  onSupplierSelected(event: AutoCompleteSelectEvent) {
    const supplier = event.value as { name: string; number: string };

    if (!supplier || supplier.name === '__noMatch__') {
      this.selectedSupplierName = '';
      this.selectedSupplierNumber = '';
      return;
    }

    this.selectedSupplierName = supplier.name;
    this.selectedSupplierNumber = supplier.number;

    const form = this.headerCaptureDataForm();
    form.get('supplier_name')?.get('value')?.setValue(supplier.name);
    form.get('supplier_id')?.get('value')?.setValue(supplier.number);
  }

  getFormFieldClassConfidenceByProp(prop: string): string {
    return this.captureDataForm.isCaptureDataFieldLowConfident(this.headerCaptureDataForm(), prop)
      ? LOW_CONFIDENCE_STYLE_CLASS
      : '';
  }

  getFormGroupByProp(prop: string): FormGroup {
    return this.captureDataForm.getCaptureFormGroupByProp(this.headerCaptureDataForm(), prop);
  }

  getDataConfidenceByProp(prop: string) {
    return this.captureDataForm.getCaptureFieldConfidenceByProp(this.headerCaptureDataForm(), prop);
  }

  getTooltipText(key: string): string {
    return this.tooltipService.getTooltip(key);
  }

  trackByKey(index: number, item: { key: string }): string {
    return item.key;
  }
}
